import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Dashboard, errorMessage } from '../api/client';
import { AuthUser, StaffDashboardData, World, isStaffUser } from '../types';
import { useServerAction } from '../hooks/useServerAction';
import { useNamesFor, useUsernames } from '../hooks/useUsernames';
import { PowerControls } from './PowerControls';
import { ShutdownCountdown } from './ShutdownCountdown';
import { Icon } from './ui/Icon';
import { Empty, Loading, Note, PlayerHead, Section, StatusPill } from './ui/primitives';
import { ActivityList } from './world/ActivityTab';
import { fmtDateTime, statusInfo, timeAgo } from '../utils/format';
import { getRegionFriendlyName } from '../utils/regionUtils';

interface Props {
  me: AuthUser;
  worlds: World[];
  loading: boolean;
  onRefresh: () => Promise<void> | void;
  onSelect: (id: string) => void;
}

type Filter = 'all' | 'active' | 'offline' | 'busy' | 'error';
const FILTERS: { id: Filter; label: string }[] = [
  { id: 'all', label: 'All' },
  { id: 'active', label: 'Active' },
  { id: 'offline', label: 'Offline' },
  { id: 'busy', label: 'Transitioning' },
  { id: 'error', label: 'Error' },
];

const matchesFilter = (w: World, f: Filter): boolean => {
  const st = statusInfo(w.status);
  switch (f) {
    case 'active':
      return st.active;
    case 'offline':
      return st.key === 'offline';
    case 'busy':
      return st.busy;
    case 'error':
      return st.key === 'error';
    default:
      return true;
  }
};

/** Staff: system stats, every world with filters/search, and per-row power controls. */
const Admin: React.FC<Props> = ({ me, worlds, loading, onRefresh, onSelect }) => {
  const [q, setQ] = useState('');
  const [filter, setFilter] = useState<Filter>('all');
  // The owner-username input is currently disabled in the toolbar, so the term never changes.
  const [ownerQ] = useState('');
  const [ownerUuid, setOwnerUuid] = useState<string | null | 'none'>(null);
  const [ownerLooking, setOwnerLooking] = useState(false);
  const [stats, setStats] = useState<StaffDashboardData | null>(null);
  const [statsError, setStatsError] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const { lookupUuid, names } = useUsernames();

  const ownerIds = useMemo(() => Array.from(new Set(worlds.map((w) => w.ownerId))), [worlds]);
  const nameOf = useNamesFor(ownerIds);

  const loadStats = useCallback(async () => {
    try {
      setStats(await Dashboard.staff());
      setStatsError(null);
    } catch (e) {
      setStatsError(errorMessage(e, 'Failed to load system stats.'));
    }
  }, []);

  useEffect(() => {
    loadStats();
  }, [loadStats]);

  // Owner search: match cached names first, otherwise ask the API for the uuid (debounced).
  useEffect(() => {
    const term = ownerQ.trim();
    if (!term) {
      setOwnerUuid(null);
      setOwnerLooking(false);
      return;
    }
    let cancelled = false;
    setOwnerLooking(true);
    const t = window.setTimeout(async () => {
      const uuid = await lookupUuid(term);
      if (!cancelled) {
        setOwnerUuid(uuid || 'none');
        setOwnerLooking(false);
      }
    }, 300);
    return () => {
      cancelled = true;
      window.clearTimeout(t);
    };
  }, [ownerQ, lookupUuid]);

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    const ownerTerm = ownerQ.trim().toLowerCase();
    return worlds.filter((w) => {
      if (!matchesFilter(w, filter)) return false;
      if (term) {
        const owner = (names[w.ownerId] || '').toLowerCase();
        if (!w.name.toLowerCase().includes(term) && !w.id.toLowerCase().includes(term) && !owner.includes(term)) return false;
      }
      if (ownerTerm) {
        const owner = (names[w.ownerId] || '').toLowerCase();
        if (owner.includes(ownerTerm)) return true;
        if (ownerUuid && ownerUuid !== 'none') return w.ownerId === ownerUuid;
        return false;
      }
      return true;
    });
  }, [worlds, filter, q, ownerQ, ownerUuid, names]);

  const counts = useMemo(
    () => ({
      all: worlds.length,
      active: worlds.filter((w) => statusInfo(w.status).active).length,
      offline: worlds.filter((w) => statusInfo(w.status).key === 'offline').length,
    }),
    [worlds]
  );

  const refresh = async () => {
    setRefreshing(true);
    try {
      await Promise.all([onRefresh(), loadStats()]);
    } finally {
      setRefreshing(false);
    }
  };

  const { request, modal, busyFor } = useServerAction(() => {
    window.setTimeout(refresh, 1500);
  });

  if (!isStaffUser(me)) {
    return (
      <div className="main2">
        <div className="page" style={{ paddingTop: 80 }}>
          <Empty icon="shield" title="Staff only" big>
            You need staff permissions to view this page.
          </Empty>
        </div>
      </div>
    );
  }

  return (
    <div className="main2">
      <div className="adm-head">
        <div>
          <h1>All worlds</h1>
          <div className="sub">
            {counts.all} worlds · {counts.active} active · {counts.offline} offline
          </div>
        </div>
        <div className="adm-tools">
          <span style={{ position: 'relative', display: 'inline-flex', alignItems: 'center' }}>
            <input className="input input--mono" placeholder="Search world, id or owner…" value={q} onChange={(e) => setQ(e.target.value)} style={{ width: 240 }} aria-label="Search worlds" />
            {/*<input className="input input--mono" placeholder="owner username" value={ownerQ} onChange={(e) => setOwnerQ(e.target.value)} style={{ width: 170 }} aria-label="Filter by owner username" />*/}
            {ownerLooking && <span className="spin" style={{ position: 'absolute', right: 10, width: 12, height: 12 }} />}
          </span>
          <div className="seg">
            {FILTERS.map((f) => (
              <button key={f.id} type="button" aria-pressed={filter === f.id} onClick={() => setFilter(f.id)}>
                {f.label}
              </button>
            ))}
          </div>
          <button type="button" className="btn btn--sm" onClick={refresh} disabled={refreshing || loading}>
            {refreshing || loading ? <span className="spin" /> : <Icon name="refresh" size={12} />} Refresh
          </button>
        </div>
      </div>

      <div className="page" style={{ paddingTop: 26 }}>
        {statsError && <Note tone="danger">{statsError}</Note>}
        {stats && (
          <div className="stats" style={{ marginBottom: 26 }}>
            <div className="stat">
              <small>Total worlds</small>
              <b>{stats.stats.totalWorlds}</b>
              <i>in the system</i>
            </div>
            <div className="stat">
              <small>Active worlds</small>
              <b>{stats.stats.loadedWorldCount}</b>
              <i>currently loaded</i>
            </div>
            <div className="stat">
              <small>Users</small>
              <b>{stats.stats.totalUsers}</b>
              <i>with at least one world</i>
            </div>
            <div className="stat">
              <small>Backups</small>
              <b>{stats.stats.totalBackupCount}</b>
              <i>stored in S3</i>
            </div>
          </div>
        )}

        {ownerQ.trim() && ownerUuid === 'none' && !ownerLooking && <Note tone="warn">No player named “{ownerQ.trim()}” was found.</Note>}

        <div className="tbl-wrap">
          <div className="tbl">
            <div className="tr tr--head">
              <span>World</span>
              <span>Owner</span>
              <span>Status</span>
              <span>Region</span>
              <span>Version</span>
              <span>Last played</span>
              <span className="ta-r">Actions</span>
            </div>
            {loading && worlds.length === 0 ? (
              <Loading />
            ) : (
              filtered.map((w) => (
                <div
                  className="tr"
                  key={w.id}
                  role="button"
                  tabIndex={0}
                  onClick={() => onSelect(w.id)}
                  onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && onSelect(w.id)}
                >
                  <span className="nm">
                    {w.name}
                    {w.isTesting && <span className="chip" style={{ marginLeft: 8 }}>testing</span>}
                    <small>{w.id}</small>
                  </span>
                  <span className="ow">
                    <PlayerHead uuid={w.ownerId} size={19} /> <span title={w.ownerId}>{nameOf(w.ownerId)}</span>
                  </span>
                  <span style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
                    <StatusPill status={w.status} short />
                    <ShutdownCountdown world={w} compact />
                  </span>
                  <span className="mono">{getRegionFriendlyName(w.region)}</span>
                  <span className="mono">{w.lastUsedVersion || '-'}</span>
                  <span className="mono" title={fmtDateTime(w.lastAccessedAt)}>
                    {w.lastAccessedAt ? timeAgo(w.lastAccessedAt) : 'never'}
                  </span>
                  <span style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 4 }}>
                    <PowerControls world={w} onRequest={request} busy={busyFor(w.id)} compact canStart canManage />
                    <Icon name="chevron" size={14} style={{ color: 'var(--ink3)' }} />
                  </span>
                </div>
              ))
            )}
            {!loading && filtered.length === 0 && (
              <Empty icon="search" title="No worlds match">
                Try a different search or filter.
              </Empty>
            )}
          </div>
        </div>

        {stats && (
          <Section title="Recent system activity" blurb="Everything that happened across all worlds in the last 24 hours.">
            <ActivityList audits={stats.stats.recentWorldActivity || []} showWorld limit={25} emptyText="No activity in the last 24 hours" />
          </Section>
        )}
      </div>
      {modal}
    </div>
  );
};

export default Admin;
